import { ReactElement, useEffect, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import {
  AbsolutePopup,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { CollapsibleIconButton } from 'partner360/components/collapsibleIconButton'
import { ReactComponent as Icon } from 'partner360/components/trainingCentre/assets/library.svg'
import { TRANSLATION_KEYS } from 'partner360/components/trainingCentre/translations'
import { ITrainingCentreData } from 'partner360/models/trainingCentre/ITrainingCentre'
import { fetchTrainingCentreData } from 'partner360/services/trainingCentre/fetchTrainingCentreData'
import { Skeleton } from 'shared/components/skeleton'

import { TrainingCentrePopup } from './components/trainingCentrePopup/TrainingCentrePopup'

import styles from './TrainingCentre.module.scss'

interface ITrainingCentreProps {
  /** Icon at rest, widening to the full label on hover and focus. For the cockpit scope panel,
   *  where the button shares a row with 13px chips and must not outweigh them. */
  collapsible?: boolean
  /** Marks the collapsible button as a widget-tour target. The component is rendered in several
   *  views, so the id belongs to whichever one runs a tour rather than being fixed here. */
  tourId?: string
  /** Draws the trigger in place of the button, for a host with its own look (the cockpit's Help &
   *  tools panel). It is disabled while the data loads and the popup opens from it as usual. */
  renderTrigger?: (state: { isOpen: boolean; disabled: boolean }) => ReactElement
}

export const TrainingCentre = ({
  collapsible = false,
  tourId,
  renderTrigger,
}: ITrainingCentreProps) => {
  const t = useSafetyTranslation()
  const [data, setData] = useState<ITrainingCentreData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpened, setOpened] = useState(false)

  useEffect(() => {
    fetchTrainingCentreData().then((res) => {
      setData(res)
      setIsLoading(false)
    })
  }, [])

  if (isLoading) {
    if (renderTrigger) {
      return renderTrigger({ isOpen: false, disabled: true })
    }

    return collapsible ? (
      <Skeleton height={32} width={32} border="50%" />
    ) : (
      <Skeleton height={37} width={185} />
    )
  }

  if (!data) {
    return null
  }

  return (
    <AbsolutePopup
      rootClassName={styles.root}
      horizontalDirection={TPopupHorizontal.Left}
      verticalDirection={TPopupVertical.Bottom}
      horizontalStartPosition={TPopupHorizontal.Right}
      verticalStartPosition={TPopupVertical.Bottom}
      visibilityTrigger={TVisibilityTrigger.Click}
      visibilityCallback={(visible) => {
        setOpened(visible)
        return visible
      }}
      popupContent={({ action }) => <TrainingCentrePopup data={data} onClose={action.close} />}
    >
      {renderTrigger ? (
        renderTrigger({ isOpen: isOpened, disabled: false })
      ) : collapsible ? (
        <CollapsibleIconButton
          icon={<Icon className={styles.icon} viewBox="0 0 24 24" />}
          label={<Translation text={TRANSLATION_KEYS.btnText} span />}
          ariaLabel={t(TRANSLATION_KEYS.btnText)}
          isOpen={isOpened}
          tourId={tourId}
        />
      ) : (
        <Button className={styles.btn}>
          <Flex alignItems={'center'}>
            <Icon />
            <Translation text={TRANSLATION_KEYS.btnText} />
          </Flex>
        </Button>
      )}
    </AbsolutePopup>
  )
}
